import React from 'react'
import {array, mixed, object} from 'yup'
import {useCurrentGuildContext, useDatxPathGenerator} from '../../../../../util/routing'
import {ServiceRequestStep} from '../../../model/ServiceRequestStep'
import {ServiceRequestFormTemplate} from '../../../genericComponents/formTemplate/ServiceRequestFormTemplate'
import {ROUTES} from '../../../../../constants/routing'
import {UploadTemplateItemCreation} from '../../../newItems/common/UploadTemplateItemCreation'
import {getItemsFromPartMatchingFile} from '../../../readImportedFile'
import {ReviewAndSubmitPartMatchingPaid} from '../ReviewAndSubmit/PartMatchingPaid'
import {MatchingSearch, PartNumberSearchDTO} from '../../../../../search/model/PartNumberSearchDTO'
import {SearchItem} from '../../../../../search/model/SearchItem'
import {useLazyGetItemMatchingQuery} from '../../../../../search/rtkSearchApi'
import * as XLSX from 'xlsx'
import * as FileSaver from 'file-saver'
import {SelfServiceDescription} from '../Description/SelfServiceDescription'
import {ItemMatchingInputDTO, NewItemInputDTO} from '../../../../model/NewItemInput'
import {ConfirmationStepSelfService} from '../ConfirmationStep/ConfirmationStepSelfService'

export interface PartMatchingItemForm {
    file: File | undefined
    items: NewItemInputDTO[] | ItemMatchingInputDTO[]
}

interface ItemMatchingOutput {
    'LINE_ID': string
    PART_NUMBER: string
    ORG_NAME: string
    PART_NUMBER_OUT: string
    ORG_NAME_OUT: string
    DATXID: string
    'TECHNICAL CLASSIFICATION': string
    DESCRIPTION: string
}

interface ItemMatchingResultDTO {
    partNumber: string
    manufacturer: string
    itemsByPartNumber: SearchItem[]
    tooManyItems: boolean
}

export const PartMatchingSelfServiceForm = () => {
    const generatePath = useDatxPathGenerator()
    const [currentGuildContext] = useCurrentGuildContext()
    const [trigger] = useLazyGetItemMatchingQuery()

    const validationSchema = object<PartMatchingItemForm>({
        file: mixed(),
        items: array().required().min(1)
    })

    const steps: ServiceRequestStep[] = [
        {
            name: 'Upload part matching self service form',
            component: <UploadTemplateItemCreation description={SelfServiceDescription}
                                                   handleFileUpload={handleFileUpload} />,
            pageValidation: validationSchema
        },
        {
            name: 'Review and Submit',
            component: <ReviewAndSubmitPartMatchingPaid isPaidForm={false} />,
            pageValidation: validationSchema
        }
    ]

    function getPartNumberLength(partNumber: string | number): number {
        if (typeof partNumber === 'number') {
            return partNumber.toString().length
        }
        return partNumber.length
    }

    function filterSearchRequest(
        search: MatchingSearch,
        searchRequestToSend: MatchingSearch[],
        searchRequestToSkip: MatchingSearch[]
    ) {
        if (getPartNumberLength(search.partNumber) >= 3) {
            searchRequestToSend.push(search)
        } else {
            searchRequestToSkip.push(search)
        }
    }

    function splitSearchRequestByPartNumber(matchingSearch: MatchingSearch[]) : {toSend: MatchingSearch[], toSkip: MatchingSearch[]} {
        const searchRequestToSend: MatchingSearch[] = []
        const searchRequestToSkip: MatchingSearch[] = []

        for (const search of matchingSearch) {
            filterSearchRequest(search, searchRequestToSend, searchRequestToSkip)
        }

        return {
            toSend: searchRequestToSend,
            toSkip: searchRequestToSkip
        }
    }

    const handleCompleteUploadForm = (itemCreation: PartMatchingItemForm): Promise<any> | undefined => {
        if (!itemCreation.file) return
        if (!itemCreation.items) return

        const matchingSearch: MatchingSearch[] = ((itemCreation?.items as ItemMatchingInputDTO[]) || [])
            .filter((item) => !!item.partNumberIn)
            .map((item) => ({
                partNumber: item.partNumberIn,
                manufacturer: item.manufacturerIn
            }))

        const {toSend, toSkip} = splitSearchRequestByPartNumber(matchingSearch)

        const partNumberSearchDTO: PartNumberSearchDTO = {
            matchingSearch: toSend,
            guildId: currentGuildContext?.guildId ?? ''
        }


        return trigger(partNumberSearchDTO).then((result) => {
            if (result.isSuccess) {
                const skipRequestResults = toSkip.map(skippedRequest => ({
                    partNumber: skippedRequest.partNumber,
                    manufacturer: skippedRequest.manufacturer ? skippedRequest.manufacturer : '',
                    itemsByPartNumber: [],
                    tooManyItems: false
                }))

                const validResult = result?.data
                const computedResult = [...validResult, ...skipRequestResults]
                formatItemMatchOutput(computedResult)
            }
            return result
        })
    }

    const formatItemMatchOutput = (itemMatching: ItemMatchingResultDTO[]) => {
        let previousPartNumber = ''
        let lineCounter = 0

        const excelData = itemMatching.reduce(
            (matchings: ItemMatchingOutput[], itemMatchingResult: ItemMatchingResultDTO) => {
                const {itemsByPartNumber, partNumber, manufacturer, tooManyItems} = itemMatchingResult
                const noItemMatch = !itemsByPartNumber?.length
                const inputItemAttributes = {
                    PART_NUMBER: partNumber,
                    ORG_NAME: manufacturer
                }

                if (partNumber !== previousPartNumber) {
                    lineCounter = lineCounter + 1
                }

                if (noItemMatch) {

                    const tooManyItemsMessage = tooManyItems ? 'Too many items. please refine your search' : 'No Result found'
                    const errorMessage = getPartNumberLength(partNumber) < 3 ? 'The part number should be at least 3 char long' :  tooManyItemsMessage

                    matchings.push({
                        'LINE_ID': lineCounter.toString(),
                        ...inputItemAttributes,
                        PART_NUMBER_OUT: errorMessage,
                        ORG_NAME_OUT: '-',
                        DATXID: '-',
                        'TECHNICAL CLASSIFICATION': '-',
                        DESCRIPTION: '-'
                    })
                } else {
                    itemsByPartNumber.forEach((item) => {
                        matchings.push({
                            'LINE_ID': lineCounter.toString(),
                            ...inputItemAttributes,
                            PART_NUMBER_OUT: item.partNumber,
                            ORG_NAME_OUT: item.definedByOrganization.label,
                            DATXID: item.id,
                            'TECHNICAL CLASSIFICATION': item.technical.label,
                            DESCRIPTION: item.description
                        })
                    })
                }
                previousPartNumber = partNumber

                return matchings
            },
            []
        )

        downloadExcel(excelData)
    }

    const downloadExcel = (excelData: ItemMatchingOutput[]) => {
        const fileName = 'datx_matching_self_service'
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        const fileExtension = '.xlsx'
        // Init worksheet
        const ws = XLSX.utils.json_to_sheet(excelData)

        XLSX.utils.sheet_add_json(ws, excelData)

        // Write to workbook
        const wb = {Sheets: {data: ws}, SheetNames: ['data']}
        const excelBuffer = XLSX.write(wb, {bookType: 'xlsx', type: 'array'})
        const data = new Blob([excelBuffer], {type: fileType})
        FileSaver.saveAs(data, fileName + fileExtension)
    }

    async function handleFileUpload(tempFile: File) {
        return await getItemsFromPartMatchingFile(tempFile)
    }

    return (
        <ServiceRequestFormTemplate
            steps={steps}
            handleFormSubmit={handleCompleteUploadForm}
            initialValues={{
                file: undefined,
                items: []
            }}
            title="Item Matching - Self-Service"
            previousUrl={generatePath(ROUTES.partMatchingServiceChoice.path)}
            optionalConfirmationStep={<ConfirmationStepSelfService />}
        ></ServiceRequestFormTemplate>
    )
}
